<template>
    <validation-observer ref="form" v-slot="{ meta }">
        <modal
            :id="modalId"
            :title="modalTitle"
            ok-title="Save"
            scrollable
            :ok-disabled="!meta.valid"
            @shown="setInitialData"
            @hidden="clearData"
            @ok.prevent="createdEditTodo"
        >
            <validation-provider v-slot="{ errors, handleChange }" name="title" rules="required" >
                <form-input label="Title" v-model="formObject.title" :error="errors[0]" @input="handleChange" />
            </validation-provider>

            <form-select label="Assignee" v-model="formObject.assignee_id" :options="assigneeList" />
            <validation-provider v-slot="{ errors, handleChange }" name="date" rules="required" >
                <form-datepicker
                    label="Due Date"
                    :hide-header="true"
                    :display-format="userUnicodeDateFormat"
                    v-model="formObject.due_date"
                    :error="errors[0]"
                    @input="handleChange"
                />
            </validation-provider>
            <p>Tags</p>
            <div class="d-flex align-items-center mb-3">
                <div
                    :key="tag.id"
                    :style="{
                        ...getTagsStyles(tag.color),
                        border: tag.selected ? `1px solid ${getTagsStyles(tag.color).color}` : 'none',
                    }"
                    v-for="tag in tagsList"
                    class="tag mr-2"
                    @click.stop="selectTag(tag)"
                >
                    <span class="mr-1">
                        {{ tag.title }}
                    </span>
                    <span
                        :style="{ color: getTagsStyles(tag.color).color }"
                        class="close"
                        v-if="tag.selected"
                        @click.stop="unSelectTag(tag)"
                    >
                        <font-awesome-icon :icon="['fas', 'xmark']" />
                    </span>
                </div>
            </div>
            <validation-provider v-slot="{ errors, handleChange }" name="todo_desc" rules="required" >
                <form-textarea
                    label="Description"
                    v-model="formObject.description"
                    :error="errors[0]"
                    @input="handleChange"
                />
            </validation-provider>
        </modal>
    </validation-observer>
</template>

<script>
    import API from '../../../../api';
    import {
        DASHBOARD_TODO_WIDGET_TAGS_LIST_ENDPOINT,
        DASHBOARD_TODO_WIDGET_ASSIGNEES_ENDPOINT,
    } from '../../../../endpoints';
    import { getTagsStyles } from '../helpers';
    import FormDatepicker from '../../../generic/form-datepicker/form-datepicker.vue';
    import formInput from '../../../generic/form-input/form-input.vue';
    import formSelect from '../../../generic/form-select/form-select.vue';
    import FormTextarea from '../../../generic/form-textarea/form-textarea.vue';
    import Modal from '../../../generic/modal/modal.vue';
    import { mapGetters } from 'vuex';

    export default {
        components: { Modal, formInput, FormDatepicker, FormTextarea, formSelect },
        name: 'CreateEditTodoModal',
        props: {
            modalId: String,
            initialValues: Object,
            modalTitle: String,
        },
        data() {
            return {
                formObject: {
                    title: null,
                    description: null,
                    due_date: null,
                    assignee_id: null,
                },
                assigneeList: [],
                tagsList: [],
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
        },
        methods: {
            clearData() {
                this.$emit('hide');
                this.formObject = {
                    title: null,
                    description: null,
                    due_date: null,
                    assignee_id: null,
                    tags: [],
                };
                this.tagsList = [];
            },
            async createdEditTodo() {
                const { tags, ...formObject } = this.formObject;
                this.$emit('submit', {
                    ...formObject,
                    tag_ids: this.tagsList.filter((item) => item.selected).map((tag) => tag.id),
                });
            },
            selectTag(tag) {
                tag.selected = true;
            },
            unSelectTag(tag) {
                tag.selected = false;
            },
            getTagsStyles: getTagsStyles,
            async fetchTags() {
                try {
                    const response = await API.get(DASHBOARD_TODO_WIDGET_TAGS_LIST_ENDPOINT());
                    this.tagsList = response.data;
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch tags' });
                }
            },
            async setInitialData() {
                try {
                    const response = await API.get(DASHBOARD_TODO_WIDGET_ASSIGNEES_ENDPOINT());
                    this.assigneeList = [
                        { value: null, text: 'Not selected' },
                        ...response.data.map((item) => ({
                            value: item.id,
                            text: item.name,
                        })),
                    ];

                    this.formObject = this.initialValues
                        ? {
                              id: this.initialValues.id,
                              title: this.initialValues.title,
                              description: this.initialValues.description,
                              due_date: this.initialValues.due_date,
                              assignee_id: this.initialValues?.assignee?.id || null,
                              tags: this.initialValues.tags,
                          }
                        : {
                              title: null,
                              description: null,
                              due_date: null,
                              assignee_id: this.assigneeList[0].value,
                              tags: [],
                          };
                    await this.fetchTags();
                    if (this.initialValues) {
                        this.tagsList = this.initialValues.tags.length
                            ? this.tagsList.map((tag) => {
                                  return {
                                      ...tag,
                                      selected: !!this.initialValues.tags.find((item) => tag.id === item.id),
                                  };
                              })
                            : (this.tagsList = this.tagsList.map((item) => ({
                                  ...item,
                                  selected: false,
                              })));
                    } else {
                        this.tagsList = this.tagsList.map((item) => ({
                            ...item,
                            selected: false,
                        }));
                    }
                    this.$refs.form.setValues(this.formObject);
                } catch (e) {
                    this.errorToast({ text: 'Could not load team members' });
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    .tag {
        font-size: 1rem;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0 7px;
        border-radius: 40px;
        font-weight: bold;
        transition: all 0.2s;
        cursor: pointer;
        &:hover {
            box-shadow: 0 3px 10px 0 #cdcdcd;
            background: transparent;
        }
        .close {
            font-size: 1rem;
            z-index: 10;
            &:hover {
                opacity: 1;
            }
        }
    }
</style>
